// AdminPanel.js
import React, { useEffect, useState, useRef } from "react";
import { db } from "./firebase";
import { ref, set, onValue } from "firebase/database";
import { v4 as uuidv4 } from "uuid";
import Sidebar from "./components/Sidebar";
import Tabs from "./components/Tabs";
import RealTimeStats from "./components/RealTimeStats";
import PreviewWithTools from "./components/PreviewWithTools";
import VideoOverlay from "./components/VideoOverlay";
import GiftsDetails from "./components/GiftsDetails";
import LiveComments from "./components/LiveComments";
import { ref as dbRef, set as dbSet } from "firebase/database";

function safeNum(v, fallback = 10) {
  return Number.isFinite(v) && !isNaN(v) ? v : fallback;
}

export default function AdminPanel() {
  const [canvas, setCanvas] = useState({
    width: 1280, height: 720, backgroundType: "none", backgroundValue: "",
    opacity: 1, gradient: { from: "#e91e63", to: "#009688", angle: 90 }, layers: [], visible: true,
    transparentBackground: true,
    groups: [] // إضافة المجموعات
  });
  const [selectedId, setSelectedId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [username, setUsername] = useState('');
  const [showGifts, setShowGifts] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const [activeTab, setActiveTab] = useState('preview');
  const [lockedLayers, setLockedLayers] = useState([]);

  const fileBgRef = useRef();
  const fileLayerImgRef = useRef();
  const fileLayerVideoRef = useRef();
  const fileLayerGifRef = useRef();
  const fileLayerAudioRef = useRef();
  const fileTextBgImgRef = useRef();
  const fileTextBgVideoRef = useRef();

  // تعريف التبويبات
  const tabs = [
    {
      id: 'preview',
      label: 'المعاينة والطبقات',
      icon: '🎨'
    },
    {
      id: 'stats',
      label: 'إحصائيات TikTok',
      icon: '📊'
    },
    {
      id: 'interactions',
      label: 'التفاعلات المباشرة',
      icon: '💬'
    },
    {
      id: 'settings',
      label: 'الإعدادات',
      icon: '⚙️'
    }
  ];

  useEffect(() => {
    return onValue(ref(db, "canvas"), (snap) => {
      if (snap.exists()) {
        const val = snap.val();
        setCanvas({
          ...val,
          gradient: val.gradient || { from: "#e91e63", to: "#009688", angle: 90 },
          opacity: val.opacity === undefined ? 1 : val.opacity,
          layers: Array.isArray(val.layers) ? val.layers : [],
          groups: Array.isArray(val.groups) ? val.groups : [],
          visible: val.visible !== false
        });
      }
    });
  }, []);

  // تحميل اسم المستخدم من Firebase
  useEffect(() => {
    const usernameRef = ref(db, 'tiktokUsername');
    const unsubscribe = onValue(usernameRef, (snapshot) => {
      if (snapshot.exists()) {
        setUsername(snapshot.val());
      }
    });

    return () => unsubscribe();
  }, []);

  const saveCanvas = (next) => set(ref(db, "canvas"), next || canvas);

  const handleUpload = async (e, type) => {
    if (!e.target.files?.[0]) return;
    setUploading(true);
    const file = e.target.files[0];
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await fetch("/upload.php", { method: "POST", body: fd });
      const data = await res.json();
      if (data.url) {
        const fullUrl = data.url;
        if (type === "bg") {
          // تحديد نوع الخلفية حسب نوع الملف
          if (file.type.startsWith("image/")) {
            setBackground("image", fullUrl);
          } else if (file.type.startsWith("video/")) {
            setBackground("video", fullUrl);
          }
          console.log('تم رفع ملف الخلفية:', fullUrl, 'نوع الملف:', file.type);
        }
        if (type === "layerImg") addLayer("image", fullUrl);
        if (type === "layerVideo") addLayer("video", fullUrl);
        if (type === "layerGif") addLayer("gif", fullUrl);
        if (type === "layerAudio") addLayer("audio", fullUrl, file.name);
        if (type === "textBgImage") {
          // تحديث خلفية النص المحدد
          if (selectedId) {
            updateLayer(selectedId, { backgroundImage: fullUrl });
          }
        }
        if (type === "textBgVideo") {
          // تحديث خلفية النص المحدد
          if (selectedId) {
            updateLayer(selectedId, { backgroundVideo: fullUrl });
          }
        }
      } else if (data.error) {
        console.error('خطأ في رفع الملف:', data.error);
        alert(`خطأ في رفع الملف: ${data.error}`);
      }
    } catch (error) {
      console.error('خطأ في رفع الملف:', error);
      alert('خطأ في رفع الملف. تأكد من أن ملف upload.php موجود ويعمل بشكل صحيح.');
    } finally {
      setUploading(false);
    }
  };

  const addLayer = (layerType, content = "", originalFileName = "") => {
    const base = {
      id: uuidv4(),
      type: layerType,
      content,
      x: 100,
      y: 100,
      width: layerType === "text" ? 350 : 320,
      height: layerType === "text" ? 80 : 180,
      zIndex: (canvas.layers?.length || 0) + 1,
      opacity: 1,
      visible: true,
      rotation: 0
    };
    if (layerType === "text") {
      base.content = "نص جديد";
      base.font = "Cairo";
      base.fontSize = 56;
      base.fontWeight = "bold";
      base.color = "#fff";
    }
    if (layerType === "audio" && originalFileName) {
      base.originalFileName = originalFileName;
    }
    const next = { ...canvas, layers: [...canvas.layers, base] };
    setCanvas(next);
    saveCanvas(next);
  };

  const updateLayer = (id, attrs) => {
    const nextLayers = canvas.layers.map((l) => {
      if (l.id !== id) return l;
      const safeAttrs = { ...attrs };
      if ('width' in safeAttrs) safeAttrs.width = safeNum(safeAttrs.width, l.width || 100);
      if ('height' in safeAttrs) safeAttrs.height = safeNum(safeAttrs.height, l.height || 100);
      if ('rotation' in safeAttrs) safeAttrs.rotation = safeNum(safeAttrs.rotation, l.rotation || 0);
      if ('opacity' in safeAttrs) safeAttrs.opacity = safeNum(safeAttrs.opacity, l.opacity ?? 1);
      return { ...l, ...safeAttrs };
    });
    const next = { ...canvas, layers: nextLayers };
    setCanvas(next);
    saveCanvas(next);
  };

  const deleteLayer = (id) => {
    const nextLayers = canvas.layers.filter((l) => l.id !== id);
    setCanvas({ ...canvas, layers: nextLayers });
    saveCanvas({ ...canvas, layers: nextLayers });
    setSelectedId(null);
  };

  const setBackground = (type, value) => {
    const next = { 
      ...canvas, 
      backgroundType: type, 
      backgroundValue: type === "none" ? "" : value 
    };
    setCanvas(next);
    saveCanvas(next);
  };

  const setGradient = (k, v) => {
    const next = { ...canvas, gradient: { ...canvas.gradient, [k]: v } };
    setCanvas(next);
    saveCanvas(next);
  };

  const setCanvasSize = (w, h) => {
    const next = { ...canvas, width: w, height: h };
    setCanvas(next);
    saveCanvas(next);
  };

  const setTransparentBackground = (transparent) => {
    const next = { ...canvas, transparentBackground: transparent };
    setCanvas(next);
    saveCanvas(next);
  };

  const setOpacity = (opacity) => {
    const next = { ...canvas, opacity: opacity };
    setCanvas(next);
    saveCanvas(next);
  };

  const resetBackground = () => {
    const next = { 
      ...canvas, 
      backgroundType: "none", 
      backgroundValue: "",
      transparentBackground: true,
      opacity: 1
    };
    setCanvas(next);
    saveCanvas(next);
    console.log('تم إعادة تعيين الخلفية إلى الشفافة');
  };

  const bringToFront = (id) => {
    const idx = canvas.layers.findIndex((l) => l.id === id);
    if (idx === -1) return;
    const nextLayers = [...canvas.layers];
    const [item] = nextLayers.splice(idx, 1);
    nextLayers.push(item);
    setCanvas({ ...canvas, layers: nextLayers });
    saveCanvas({ ...canvas, layers: nextLayers });
  };

  const setAllLayersVisible = (isVisible) => {
    const nextLayers = canvas.layers.map(l => ({ ...l, visible: isVisible }));
    const next = { ...canvas, layers: nextLayers };
    setCanvas(next);
    saveCanvas(next);
  };
  
  const playAudio = (audioLayerId) => {
    dbSet(dbRef(db, "playAudioId"), audioLayerId);
  };

  // قفل/فتح طبقة
  const toggleLockLayer = (id) => {
    setLockedLayers((prev) =>
      prev.includes(id) ? prev.filter(lid => lid !== id) : [...prev, id]
    );
  };

  // إضافة مجموعة جديدة
  const addGroup = (name = "مجموعة جديدة") => {
    const newGroup = {
      id: uuidv4(),
      name: name,
      visible: true,
      layers: [],
      createdAt: new Date().toISOString()
    };
    const next = { ...canvas, groups: [...canvas.groups, newGroup] };
    setCanvas(next);
    saveCanvas(next);
    return newGroup.id;
  };

  // حذف مجموعة
  const deleteGroup = (groupId) => {
    const group = canvas.groups.find(g => g.id === groupId);
    if (group) {
      // حذف جميع الطبقات في المجموعة
      const layersToRemove = group.layers;
      const nextLayers = canvas.layers.filter(l => !layersToRemove.includes(l.id));
      
      // حذف المجموعة
      const nextGroups = canvas.groups.filter(g => g.id !== groupId);
      
      const next = { 
        ...canvas, 
        layers: nextLayers,
        groups: nextGroups 
      };
      setCanvas(next);
      saveCanvas(next);
    }
  };

  // إضافة طبقة إلى مجموعة
  const addLayerToGroup = (layerId, groupId) => {
    const nextGroups = canvas.groups.map(group => {
      if (group.id === groupId) {
        return { ...group, layers: [...group.layers, layerId] };
      }
      return group;
    });
    const next = { ...canvas, groups: nextGroups };
    setCanvas(next);
    saveCanvas(next);
  };

  // إزالة طبقة من مجموعة
  const removeLayerFromGroup = (layerId, groupId) => {
    const nextGroups = canvas.groups.map(group => {
      if (group.id === groupId) {
        return { ...group, layers: group.layers.filter(id => id !== layerId) };
      }
      return group;
    });
    const next = { ...canvas, groups: nextGroups };
    setCanvas(next);
    saveCanvas(next);
  };

  // إظهار/إخفاء مجموعة
  const toggleGroupVisibility = (groupId) => {
    const nextGroups = canvas.groups.map(group => {
      if (group.id === groupId) {
        return { ...group, visible: !group.visible };
      }
      return group;
    });
    const next = { ...canvas, groups: nextGroups };
    setCanvas(next);
    saveCanvas(next);
  };

  // تحديث اسم المجموعة
  const updateGroupName = (groupId, newName) => {
    const nextGroups = canvas.groups.map(group => {
      if (group.id === groupId) {
        return { ...group, name: newName };
      }
      return group;
    });
    const next = { ...canvas, groups: nextGroups };
    setCanvas(next);
    saveCanvas(next);
  };

  // إظهار كل الطبقات (ما عدا المقفولة)
  const showAllLayers = () => {
    const nextLayers = canvas.layers.map(l =>
      lockedLayers.includes(l.id) ? { ...l, visible: true } : { ...l, visible: true }
    );
    const next = { ...canvas, layers: nextLayers };
    setCanvas(next);
    saveCanvas(next);
  };

  // إخفاء كل الطبقات (ما عدا المقفولة)
  const hideAllLayers = () => {
    const nextLayers = canvas.layers.map(l =>
      lockedLayers.includes(l.id) ? { ...l, visible: true } : { ...l, visible: false }
    );
    const next = { ...canvas, layers: nextLayers };
    setCanvas(next);
    saveCanvas(next);
  };

  // عرض محتوى التبويب النشط
  const renderTabContent = () => {
    switch (activeTab) {
      case 'preview':
                return (
          <div className="preview-tab-content">
            {/* المعاينة مع أدوات الطبقات */}
            <PreviewWithTools
              canvas={canvas}
              selectedId={selectedId}
              setSelectedId={setSelectedId}
              updateLayer={updateLayer}
              deleteLayer={deleteLayer}
              bringToFront={bringToFront}
              addLayer={addLayer}
              fileLayerImgRef={fileLayerImgRef}
              fileLayerVideoRef={fileLayerVideoRef}
              fileLayerGifRef={fileLayerGifRef}
              fileLayerAudioRef={fileLayerAudioRef}
              fileTextBgImgRef={fileTextBgImgRef}
              fileTextBgVideoRef={fileTextBgVideoRef}
              handleUpload={handleUpload}
              lockedLayers={lockedLayers}
              toggleLockLayer={toggleLockLayer}
              showAllLayers={showAllLayers}
              hideAllLayers={hideAllLayers}
              addGroup={addGroup}
              deleteGroup={deleteGroup}
              addLayerToGroup={addLayerToGroup}
              removeLayerFromGroup={removeLayerFromGroup}
              toggleGroupVisibility={toggleGroupVisibility}
              updateGroupName={updateGroupName}
            />

            {/* معالج الفيديو */}
            <div className="video-audio-container" style={{
              background: 'var(--bg-secondary)',
              borderRadius: '15px',
              padding: '20px',
              marginTop: '20px',
              boxShadow: '0 4px 15px var(--shadow-color)',
              border: '1px solid var(--border-color)'
            }}>
              <h3 style={{
                margin: '0 0 15px 0',
                color: 'var(--text-primary)',
                fontSize: '16px',
                fontWeight: 'bold',
                textAlign: 'center'
              }}>
                🎵 معالج الصوت والفيديو
              </h3>
              
              <VideoOverlay layers={canvas.layers} selectedId={selectedId} showAudioControls={true} canvas={canvas} />
            </div>
          </div>
        );

      case 'stats':
        return (
          <div className="stats-tab-content">
            <RealTimeStats />
          </div>
        );

      case 'interactions':
        return (
          <div className="interactions-tab-content">
            {username ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                {/* أزرار التحكم في الهدايا والتعليقات */}
                <div className="gifts-comments-toggle" style={{
                  background: 'var(--bg-secondary)',
                  borderRadius: '15px',
                  padding: '20px',
                  boxShadow: '0 4px 15px var(--shadow-color)',
                  border: '1px solid var(--border-color)'
                }}>
                  <h3 style={{
                    margin: '0 0 15px 0',
                    color: 'var(--text-primary)',
                    fontSize: '16px',
                    fontWeight: 'bold',
                    textAlign: 'center'
                  }}>
                    🎮 التحكم في التفاعلات
                  </h3>
                  
                  <div style={{
                    display: 'flex',
                    gap: '10px',
                    justifyContent: 'center',
                    flexWrap: 'wrap'
                  }}>
                    <button
                      onClick={() => setShowGifts(!showGifts)}
                      style={{
                        background: showGifts ? 'var(--button-bg)' : 'var(--bg-primary)',
                        color: showGifts ? 'white' : 'var(--text-primary)',
                        border: '1px solid var(--border-color)',
                        borderRadius: '10px',
                        padding: '12px 20px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: 'bold',
                        transition: 'all 0.3s ease'
                      }}
                    >
                      {showGifts ? '🔴 إخفاء الهدايا' : '🎁 عرض الهدايا'}
                    </button>
                    
                    <button
                      onClick={() => setShowComments(!showComments)}
                      style={{
                        background: showComments ? 'var(--button-bg)' : 'var(--bg-primary)',
                        color: showComments ? 'white' : 'var(--text-primary)',
                        border: '1px solid var(--border-color)',
                        borderRadius: '10px',
                        padding: '12px 20px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: 'bold',
                        transition: 'all 0.3s ease'
                      }}
                    >
                      {showComments ? '🔴 إخفاء التعليقات' : '💬 عرض التعليقات'}
                    </button>
                  </div>
                </div>
                
                {/* تفاصيل الهدايا */}
                {showGifts && (
                  <div className="gifts-details" style={{
                    background: 'var(--bg-secondary)',
                    borderRadius: '15px',
                    padding: '20px',
                    boxShadow: '0 4px 15px var(--shadow-color)',
                    border: '1px solid var(--border-color)'
                  }}>
                    <h3 style={{
                      margin: '0 0 15px 0',
                      color: 'var(--text-primary)',
                      fontSize: '16px',
                      fontWeight: 'bold',
                      textAlign: 'center'
                    }}>
                      🎁 تفاصيل الهدايا
                    </h3>
                    
                    <GiftsDetails username={username} isVisible={showGifts} />
                  </div>
                )}
                
                {/* التعليقات المباشرة */}
                {showComments && (
                  <div className="live-comments" style={{
                    background: 'var(--bg-secondary)',
                    borderRadius: '15px',
                    padding: '20px',
                    boxShadow: '0 4px 15px var(--shadow-color)',
                    border: '1px solid var(--border-color)'
                  }}>
                    <h3 style={{
                      margin: '0 0 15px 0',
                      color: 'var(--text-primary)',
                      fontSize: '16px',
                      fontWeight: 'bold',
                      textAlign: 'center'
                    }}>
                      💬 التعليقات المباشرة
                    </h3>
                    
                    <LiveComments username={username} isVisible={showComments} />
                  </div>
                )}
              </div>
            ) : (
              <div style={{
                background: 'var(--bg-secondary)',
                borderRadius: '15px',
                padding: '40px',
                textAlign: 'center',
                boxShadow: '0 4px 15px var(--shadow-color)',
                border: '1px solid var(--border-color)'
              }}>
                <div style={{ fontSize: '48px', marginBottom: '20px' }}>👤</div>
                <h3 style={{
                  margin: '0 0 15px 0',
                  color: 'var(--text-primary)',
                  fontSize: '18px',
                  fontWeight: 'bold'
                }}>
                  يرجى إدخال اسم مستخدم TikTok أولاً
                </h3>
                <p style={{
                  color: 'var(--text-secondary)',
                  fontSize: '14px',
                  margin: 0
                }}>
                  انتقل إلى تبويب "إحصائيات TikTok" لإدخال اسم المستخدم
                </p>
              </div>
            )}
          </div>
        );

      case 'settings':
        return (
          <div className="settings-tab-content">
            <div style={{
              background: 'var(--bg-secondary)',
              borderRadius: '15px',
              padding: '20px',
              boxShadow: '0 4px 15px var(--shadow-color)',
              border: '1px solid var(--border-color)'
            }}>
              <h3 style={{
                margin: '0 0 15px 0',
                color: 'var(--text-primary)',
                fontSize: '16px',
                fontWeight: 'bold',
                textAlign: 'center'
              }}>
                ⚙️ إعدادات الكنفا والخلفية
              </h3>
              
                    <Sidebar
        canvas={canvas}
        setBackground={setBackground}
        setGradient={setGradient}
        setCanvasSize={setCanvasSize}
        setTransparentBackground={setTransparentBackground}
        setOpacity={setOpacity}
        resetBackground={resetBackground}
        addLayer={addLayer}
        uploading={uploading}
        fileBgRef={fileBgRef}
        fileLayerImgRef={fileLayerImgRef}
        fileLayerVideoRef={fileLayerVideoRef}
        fileLayerGifRef={fileLayerGifRef}
        handleUpload={handleUpload}
      />
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div 
      data-admin-panel
      style={{ 
        background: "var(--bg-primary)", 
        minHeight: "100vh", 
        fontFamily: "Cairo, Arial", 
        direction: "rtl",
        padding: "10px"
      }}
    >
      <header style={{
        background: "var(--header-bg)", 
        color: "#fff", 
        padding: "15px 10px",
        fontSize: "clamp(16px, 4vw, 20px)", 
        textAlign: "center", 
        fontWeight: "bold", 
        letterSpacing: 1, 
        marginBottom: 20,
        borderRadius: "15px",
        boxShadow: "0 2px 10px var(--shadow-color)"
      }}>
        🎬 إدارة التيكر للبث المباشر
      </header>
      
      {/* التبويبات */}
      <Tabs 
        activeTab={activeTab}
        onTabChange={setActiveTab}
        tabs={tabs}
      />
      
      {/* محتوى التبويب النشط */}
      <div className="tab-content" style={{
        maxWidth: "1400px",
        margin: "0 auto"
      }}>
        {renderTabContent()}
      </div>
    </div>
  );
}
